import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useActiveOrganization } from "@/hooks/organizations";
import { useWorkspaces } from "@/queries";
import {
  useCreateOrganization,
  useOrganizations,
} from "@/queries/organizations";
import { BaseOrganization } from "@/types/organizations";
import { PlusIcon } from "@heroicons/react/outline";
import { ChevronDownIcon } from "@heroicons/react/solid";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";
import { Button } from "./ui/button";
import { OverflowDisplayTooltip } from "./ui/Tooltip/OverflowDisplayTooltip";

const CircleLetter = ({
  letter,
  selected,
}: {
  letter: string;
  selected: boolean;
}) => (
  <div
    className={`mr-2 flex h-6 w-6 items-center justify-center rounded-full border border-gray-50 bg-white shadow-sm`}
  >
    <span
      className={`text-sm font-medium uppercase ${selected && "text-blue-500"}`}
    >
      {letter}
    </span>
  </div>
);

export const OrganizationSelector = () => {
  const organizationsQuery = useOrganizations();
  const organizations = organizationsQuery.data?.items || [];
  const activeOrganization = useActiveOrganization();
  const workspace = useWorkspaces();

  const defaultWorkspaceLink = (organization_id: number) => {
    const defaultWorkspace = workspace.data?.workspaces.find(
      (workspace) =>
        workspace.is_default && workspace.organization_id === organization_id,
    );
    if (!defaultWorkspace) return "/home";
    return `/workspace/${defaultWorkspace.id}`;
  };

  return (
    <Dialog>
      <DropdownMenu>
        <DropdownMenuTrigger className="w-40 rounded-lg border border-gray-300 bg-transparent p-2 text-base focus:outline-none focus:ring-0 sm:text-sm 2xl:w-64">
          <div className="flex items-center justify-between px-1 font-medium text-gray-800">
            <div className="flex items-center">
              <div className="">
                <CircleLetter
                  letter={(activeOrganization?.name ?? "O").charAt(0)}
                  selected={false}
                />
              </div>
              <div className="flex w-20 items-center 2xl:w-40">
                {organizationsQuery.isLoading ? (
                  "Loading..."
                ) : (
                  <OverflowDisplayTooltip
                    content={activeOrganization?.name}
                    side="bottom"
                    className="inline-block w-full"
                  >
                    <>{activeOrganization?.name}</>
                  </OverflowDisplayTooltip>
                )}
              </div>
            </div>
            <ChevronDownIcon className="font-base h-5 w-5 text-gray-400" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="z-20 mt-1 max-h-[450px] w-64 overflow-auto rounded-lg bg-white px-3 py-2 text-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {organizations.length !== 0 && (
            <div className="mb-1 py-1 text-center text-gray-400">
              Switch organizations
            </div>
          )}
          {organizations.map((organization) => (
            <DropdownMenuItem asChild key={organization.id}>
              <Link to={defaultWorkspaceLink(organization.id)}>
                <div
                  className={`mb-1 flex cursor-pointer select-none items-center rounded-md px-3 py-2 ${
                    organization.id === activeOrganization?.id
                      ? "bg-gray-200"
                      : "text-gray-800"
                  } ${
                    organization.id === activeOrganization?.id
                      ? "bg-gray-100"
                      : ""
                  }`}
                >
                  <CircleLetter
                    letter={(organization.name ?? "O").charAt(0)}
                    selected={organization.id === activeOrganization?.id}
                  />
                  <div
                    className={`block flex-1 overflow-hidden pr-1 font-medium tracking-normal ${
                      organization.id === activeOrganization?.id
                        ? "text-gray-800"
                        : "text-gray-500"
                    } ${
                      organization.id === activeOrganization?.id &&
                      "text-gray-900"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <OverflowDisplayTooltip
                        content={organization.name}
                        side="top"
                      >
                        <>{organization.name}</>
                      </OverflowDisplayTooltip>
                    </div>
                  </div>
                </div>
              </Link>
            </DropdownMenuItem>
          ))}
          {organizationsQuery.isLoading && (
            <div className="flex items-center justify-center py-5">
              <LoadingSpinner size={5} />
            </div>
          )}
          <CreateOrganization />
        </DropdownMenuContent>
      </DropdownMenu>
    </Dialog>
  );
};

const CreateOrganization = () => {
  return (
    <>
      <DialogTrigger asChild>
        <div className="my-2 flex w-full justify-center pt-2">
          <Button
            variant="secondaryLightOutline"
            size="default"
            className="w-full text-left"
          >
            <PlusIcon className="mr-1 h-4 w-4" aria-hidden="true" />
            <span>Create New Organization</span>
          </Button>
        </div>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
        </DialogHeader>
        <CreateOrganizationForm />
      </DialogContent>
    </>
  );
};

const CreateOrganizationForm = () => {
  const create = useCreateOrganization();
  const form = useForm<BaseOrganization>({
    defaultValues: {
      name: "",
    },
  });

  const handleSubmit = (data: BaseOrganization) => {
    create.mutate(data);
  };
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <Input placeholder="Enter organization name" {...field} />
            </FormItem>
          )}
        />
        <DialogFooter className="mt-3">
          <Button type="submit">Submit</Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
